import { useLocation } from "react-router-dom";
import { Star, LogOut, Volume2, VolumeX, Shield, Sparkles } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { 
  Sidebar, 
  SidebarContent, 
  SidebarGroup, 
  SidebarGroupContent, 
  SidebarGroupLabel, 
  SidebarMenu, 
  SidebarMenuButton, 
  SidebarMenuItem, 
  SidebarFooter, 
  SidebarHeader, 
  SidebarSeparator, 
  useSidebar 
} from "@/components/ui/sidebar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LanguageSelector } from "@/components/LanguageSelector";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useGlobalSound } from "@/hooks/useGlobalSound";
import { useFavoriteMenus } from "@/hooks/useFavoriteMenus";
import { useUnreadSupportCount } from "@/hooks/useUnreadSupportCount";
import { cn } from "@/lib/utils";
import { getLocalizedAdminMenuGroups } from "@/config/adminRoutes";

const GROUP_COLORS = [
  { dot: 'bg-[hsl(var(--admin-primary))]', active: 'from-[hsl(var(--admin-primary))]' },
  { dot: 'bg-[hsl(var(--admin-success))]', active: 'from-[hsl(var(--admin-success))]' },
  { dot: 'bg-[hsl(var(--admin-purple))]', active: 'from-[hsl(var(--admin-purple))]' },
  { dot: 'bg-[hsl(var(--admin-warning))]', active: 'from-[hsl(var(--admin-warning))]' },
  { dot: 'bg-[hsl(var(--admin-info))]', active: 'from-[hsl(var(--admin-info))]' },
  { dot: 'bg-[hsl(var(--admin-danger))]', active: 'from-[hsl(var(--admin-danger))]' },
];

export function AdminSidebar() {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const location = useLocation();
  const currentPath = location.pathname;
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { isSoundEnabled, toggleSound } = useGlobalSound();
  const { favoriteMenus, toggleFavoriteMenu, isFavorite } = useFavoriteMenus();
  const { unreadCount: supportUnreadCount } = useUnreadSupportCount();

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      toast.success(t('navigation.logout') || 'ออกจากระบบสำเร็จ');
      navigate("/admin/login");
    } catch (error) {
      console.error("Logout error:", error);
      toast.error(t('common.error') || 'เกิดข้อผิดพลาด');
    }
  };

  const menuGroups = getLocalizedAdminMenuGroups(t);
  const isActive = (path: string) => currentPath === path;

  const allItems = menuGroups.flatMap(group => group.items);
  const favoriteItems = allItems.filter(item => isFavorite(item.path));

  const renderMenuItem = (item: typeof allItems[0], showFavStar = true) => {
    const active = isActive(item.path);
    const Icon = item.icon;
    
    const MenuButton = (
      <button 
        onClick={() => navigate(item.path)} 
        className={cn(
          "flex items-center rounded-xl transition-all duration-200 w-full relative group",
          collapsed 
            ? "justify-center px-2 py-2.5 gap-0"
            : "gap-3 px-3 py-2.5 text-left",
          active 
            ? "bg-gradient-to-r from-[hsl(var(--admin-primary))]/15 to-[hsl(var(--admin-primary))]/5 text-[hsl(var(--admin-primary))] font-semibold shadow-sm ring-1 ring-[hsl(var(--admin-primary))]/20" 
            : "text-foreground/70 hover:bg-muted/60 hover:text-foreground"
        )}
      >
        <div className={cn(
          "relative flex-shrink-0 rounded-lg flex items-center justify-center transition-all",
          collapsed ? "w-8 h-8" : "w-9 h-9",
          active 
            ? "bg-[hsl(var(--admin-primary))] text-white shadow-md" 
            : "bg-muted/50 text-muted-foreground group-hover:bg-[hsl(var(--admin-primary))]/10 group-hover:text-[hsl(var(--admin-primary))]"
        )}>
          <Icon className={cn("transition-all", collapsed ? "h-4 w-4" : "h-4.5 w-4.5")} />
          {collapsed && isFavorite(item.path) && (
            <Star className="h-2 w-2 fill-yellow-500 text-yellow-500 absolute -top-0.5 -right-0.5" />
          )}
        </div>
        {!collapsed && (
          <>
            <span className="text-sm flex-1 font-medium truncate">{item.label}</span>
            {item.path === '/admin/support' && supportUnreadCount > 0 && (
              <Badge 
                variant="destructive" 
                className="min-w-5 h-5 text-[10px] px-1 flex items-center justify-center animate-pulse"
              >
                {supportUnreadCount > 99 ? '99+' : supportUnreadCount}
              </Badge>
            )}
            {showFavStar && (
              <button 
                onClick={e => {
                  e.stopPropagation();
                  toggleFavoriteMenu(item.path);
                }} 
                className="opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Star className={cn(
                  "h-3.5 w-3.5 transition-all hover:scale-110",
                  isFavorite(item.path) 
                    ? "fill-yellow-500 text-yellow-500" 
                    : "text-muted-foreground hover:text-yellow-500"
                )} />
              </button>
            )}
          </>
        )}
      </button>
    );
    
    return (
      <SidebarMenuItem key={item.path}>
        <SidebarMenuButton asChild>
          {collapsed ? (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>{MenuButton}</TooltipTrigger>
              <TooltipContent side="right" className="font-normal text-black dark:text-white">
                {item.label}
              </TooltipContent>
            </Tooltip>
          ) : MenuButton}
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return (
    <Sidebar collapsible="icon">
      {/* Premium Gradient Header */}
      <SidebarHeader className="border-b-0">
        <div className={cn(
          "admin-sidebar-header rounded-b-2xl mx-2 mt-2 mb-1 p-4 text-white",
          collapsed && "p-2 mx-1 rounded-xl"
        )}>
          <div className="flex items-center gap-3">
            <div className={cn(
              "rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-inner flex-shrink-0",
              collapsed ? "w-8 h-8" : "w-10 h-10"
            )}>
              <Shield className={cn(collapsed ? "h-4 w-4" : "h-5 w-5")} />
            </div>
            {!collapsed && (
              <div className="min-w-0">
                <h2 className="font-bold text-sm tracking-wide">SmartPay</h2>
                <p className="text-[10px] text-white/70 font-medium">Admin Console</p>
              </div>
            )}
          </div>
        </div>
      </SidebarHeader>
      
      <SidebarContent className="px-1">
        {/* Favorites */}
        {favoriteItems.length > 0 && (
          <div>
            <SidebarGroup>
              <SidebarGroupLabel className={collapsed ? "hidden" : "text-[10px] uppercase text-muted-foreground/60 font-bold tracking-[0.15em] px-3 py-1.5 flex items-center gap-2"}>
                <Sparkles className="h-3 w-3 text-yellow-500" />
                <span>Favorites</span>
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu className="gap-0.5 px-1">
                  {favoriteItems.map(item => renderMenuItem(item, true))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
            <SidebarSeparator className="my-1.5 opacity-50" />
          </div>
        )}

        {/* Menu Groups */}
        {menuGroups.map((group, groupIndex) => {
          const groupColor = GROUP_COLORS[groupIndex % GROUP_COLORS.length];
          return (
            <div key={groupIndex}>
              <SidebarGroup>
                <SidebarGroupLabel className={collapsed ? "hidden" : "text-[10px] uppercase text-muted-foreground/60 font-bold tracking-[0.15em] px-3 py-1.5 flex items-center gap-2"}>
                  <span className={cn("w-1.5 h-1.5 rounded-full", groupColor.dot)} />
                  <span>{group.title}</span>
                </SidebarGroupLabel>
                
                <SidebarGroupContent>
                  <SidebarMenu className="gap-0.5 px-1">
                    {group.items.map(item => renderMenuItem(item))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
              {groupIndex < menuGroups.length - 1 && <SidebarSeparator className="my-1.5 opacity-50" />}
            </div>
          );
        })}
      </SidebarContent>
      
      {/* Footer */}
      <SidebarFooter className="border-t border-border/30">
        <div className={cn(
          "p-3",
          collapsed ? "flex flex-col items-center gap-1.5" : "space-y-1.5"
        )}>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size={collapsed ? "icon" : "sm"}
                onClick={toggleSound}
                className={cn(
                  "transition-all rounded-xl h-9",
                  collapsed ? "w-9" : "w-full justify-start gap-2"
                )}
              >
                {isSoundEnabled ? (
                  <Volume2 className="h-4 w-4 text-[hsl(var(--admin-success))]" />
                ) : (
                  <VolumeX className="h-4 w-4 text-muted-foreground" />
                )}
                {!collapsed && <span className="text-xs">{isSoundEnabled ? "เสียงเปิด" : "เสียงปิด"}</span>}
              </Button>
            </TooltipTrigger>
            {collapsed && <TooltipContent side="right">{isSoundEnabled ? "เสียงเปิด" : "เสียงปิด"}</TooltipContent>}
          </Tooltip>

          {!collapsed && <ThemeToggle />}
          {!collapsed && <LanguageSelector />}

          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size={collapsed ? "icon" : "sm"}
                onClick={handleLogout}
                className={cn(
                  "transition-all rounded-xl h-9 text-[hsl(var(--admin-danger))] hover:bg-[hsl(var(--admin-danger))]/10",
                  collapsed ? "w-9" : "w-full justify-start gap-2"
                )}
              >
                <LogOut className="h-4 w-4" />
                {!collapsed && <span className="text-xs">ออกจากระบบ</span>}
              </Button>
            </TooltipTrigger>
            {collapsed && <TooltipContent side="right">ออกจากระบบ</TooltipContent>}
          </Tooltip>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
